import {
  createModuleInstance,
  effectiveRarity,
  type FusedComponent,
  type ModuleInstance,
} from "../models/ModuleInstance.js";
import {
  RARITY_ORDER,
  type ModuleRarity,
  type ProjectileModule,
} from "../modules/ProjectileModule.js";
import type { ModuleSlotType } from "../models/ModuleTypes.js";
import type { ProjectileSpec } from "../models/ProjectileSpec.js";
import type { ModuleProcKind } from "../modules/ModuleEffect.js";
import { type DefinitionLookup, type MaterialButtonState } from "./ModuleMerge.js";
import type { ModuleInventory } from "./ModuleInventory.js";
import type { ModuleGridController } from "./ModuleGridController.js";

/**
 * 모듈 융합 순수 로직 — Phaser 비의존(단위 테스트 대상).
 * "같은 그리드 점유 모양(footprintId)이면 같은/다른 모듈 2개 + 비용(광고/다이아)을 소모해 합쳐 더 강한 모듈".
 * 결과물은 두 재료의 effect를 **모두 적용**하는 융합 모듈(FusedModule) — ModuleInstance.fused에 컴포넌트 2개를 보존한다.
 * 합성(ModuleMerge)과 달리 등급/이름 제약이 없고 footprint만 일치하면 된다. 비용·일일 카운터는 ModuleFusionController.
 * 융합 산출물(inst.fused 보유)은 **다음부터 합성·융합 재료에서 제외**(canFuse/canMerge가 배제).
 */

/** 융합에 필요한 재료 개수 (같은 footprint 2개 → 더 강한 모듈 1개). */
export const FUSION_MATERIAL_COUNT = 2;

/** 두 등급 중 더 높은 등급 (표시색용). RARITY_ORDER가 SSoT. */
function higherRarity(a: ModuleRarity, b: ModuleRarity): ModuleRarity {
  return RARITY_ORDER.indexOf(a) >= RARITY_ORDER.indexOf(b) ? a : b;
}

/**
 * 융합 가능 여부 — 재료가 정확히 2개 + 서로 다른 instanceId + 같은 footprintId + 둘 다 융합 산출물(fused) 아님.
 * 등급·definitionId 제약 없음("같은 혹은 다른 모듈" 허용). 하나라도 어긋나면 false.
 */
export function canFuse(materials: readonly ModuleInstance[]): boolean {
  if (materials.length !== FUSION_MATERIAL_COUNT) return false;
  const [a, b] = materials;
  if (!a || !b) return false;
  if (a.instanceId === b.instanceId) return false;
  if (a.fused || b.fused) return false; // 융합 산출물은 재료 제외
  return a.footprintId === b.footprintId;
}

export interface FusionResult {
  readonly fused: ModuleInstance;
  readonly consumedIds: readonly string[];
}

/**
 * 재료 2개를 융합해 컴포넌트 2개를 가진 융합 인스턴스 1개를 만든다 (순수). 호출 전 canFuse로 가드.
 * 컴포넌트 rarity = 재료의 effectiveRarity(합성 오버라이드 반영). ⚠ level은 컴포넌트에 저장하지 않는다 —
 * 레벨은 플레이어 자산이라 전투 시 컴포넌트 definitionId의 player level을 조회한다(2축 분리).
 * top-level: definitionId/footprintId = 첫 재료(표시 base·공유 footprint), rarity = 최고 등급(표시색).
 */
export function fuseModules(
  materials: readonly ModuleInstance[],
  defOf: DefinitionLookup,
  nextInstanceId: string,
): FusionResult {
  if (!canFuse(materials)) {
    throw new Error("[ModuleFusion] cannot fuse: invalid materials (need 2 distinct unfused same-footprint)");
  }
  const components: FusedComponent[] = materials.map((m) => ({
    definitionId: m.definitionId,
    rarity: effectiveRarity(m, defOf(m.definitionId)),
  }));
  const first = materials[0]!;
  const displayRarity = components.reduce<ModuleRarity>(
    (acc, c) => higherRarity(acc, c.rarity),
    RARITY_ORDER[0]!, // 최저 등급에서 시작(higherRarity가 max로 끌어올림) — components 비어있음 방어
  );
  const fused = createModuleInstance({
    definitionId: first.definitionId,
    instanceId: nextInstanceId,
    rarity: displayRarity,
    footprintId: first.footprintId,
    fused: components,
  });
  return { fused, consumedIds: materials.map((m) => m.instanceId) };
}

/**
 * 융합 재료 후보 — 전투 그리드 미배치 + 융합 산출물(fused) 아님. 융합 탭 후보 그리드 + 융합 직전 재검증 SSoT.
 * 융합 산출물도 그리드 배치·전투 사용은 가능하지만, 재료로는 다시 쓸 수 없다(사용자 규칙).
 */
export function fusionCandidates(
  inventory: ModuleInventory,
  grid: ModuleGridController,
): ModuleInstance[] {
  const placed = new Set(grid.placedInstanceIds());
  return inventory.allInstances().filter((i) => !placed.has(i.instanceId) && !i.fused);
}

/** 융합 탭 셀 행의 최소 식별 필드 (선택 검증은 footprint 기준). */
export interface FusionSelectableRow {
  readonly instanceId: string;
  readonly footprintId: string;
}

/**
 * 융합 탭 정보 팝업 등록 버튼 상태 — 같은 footprint·2슬롯 기준 (resolveMaterialButtonState의 fusion 변형, 순수).
 * - 이미 선택 → unregister(해제 가능) / 슬롯 만석 미선택 → disabled / 기준과 footprint 다르면 disabled / 그 외 register-enabled.
 */
export function resolveFusionButtonState(
  selectedIds: readonly string[],
  rows: readonly FusionSelectableRow[],
  targetId: string,
  materialCount: number = FUSION_MATERIAL_COUNT,
): MaterialButtonState {
  const target = rows.find((r) => r.instanceId === targetId);
  if (!target) return { action: "register", enabled: false };
  if (selectedIds.includes(targetId)) return { action: "unregister", enabled: true };
  if (selectedIds.length >= materialCount) return { action: "register", enabled: false };
  const baseId = selectedIds[0];
  const base = baseId ? rows.find((r) => r.instanceId === baseId) : undefined;
  if (base && target.footprintId !== base.footprintId) {
    return { action: "register", enabled: false };
  }
  return { action: "register", enabled: true };
}

/**
 * 융합 모듈(composite ProjectileModule) — 컴포넌트 sub-module들을 순서대로 모두 apply한다 ("그냥 합침").
 * 각 컴포넌트의 chance 게이트는 독립 보존(effect 데이터 병합이 아니라 둘 다 apply → 게이트 합성 정보 손실 없음).
 * CombatSystem.buildSpec이 fused 인스턴스의 컴포넌트별 ModuleRegistry.create로 sub-module을 만들어 주입한다.
 * v1: 파이프라인 강화(catalyst/precision) 메타는 미참여(opaque) — 추후 확장.
 */
export class FusedModule implements ProjectileModule {
  constructor(
    private readonly displayId: string,
    readonly level: number,
    private readonly components: readonly ProjectileModule[],
    private readonly slot: ModuleSlotType = "quantity",
  ) {}

  get id(): string {
    return this.displayId;
  }
  get displayName(): string {
    return this.displayId;
  }
  get description(): string {
    return "";
  }
  get slotType(): ModuleSlotType {
    return this.slot;
  }

  apply(
    spec: ProjectileSpec,
    rng?: () => number,
    onProc?: (kind: ModuleProcKind) => void,
    chanceBonus?: number,
  ): ProjectileSpec {
    let s = spec;
    for (const c of this.components) {
      s = c.apply(s, rng, onProc, chanceBonus);
    }
    return s;
  }
}
